/**
 * client/src/lib/memory-confirm-card-storage.ts
 *
 * sessionStorage-backed dismissal for MemoryConfirmCard (memory-capture-flow
 * spec 03, AC-21: "Later" hides until the next sign-in). Same read/write-with-
 * try/catch shape as backup-passkey-nudge-storage.ts: Web Storage may be
 * disabled or over quota, and that must never throw into React. No server
 * write — the card is a nudge, not a record.
 *
 * "UNTIL THE NEXT SIGN-IN" ON THE CLIENT. sessionStorage is per TAB, so it
 * outlives a sign-out + re-sign-in in the same tab and would leak across users
 * on a shared device. /api/auth/me carries no per-login identifier on main
 * (only `sessionCredentialId`, which is the same after re-signing in with the
 * same passkey), so the dismissal is:
 *   1. bound to the USER id — another user never inherits it; and
 *   2. released by clearConfirmCardDismissed(), called from the two places that
 *      end a login on the client: handleLogout (explicit sign-out) and the
 *      session-death 401 path in queryClient.ts (10-min inactivity / 30-min
 *      max / eviction).
 * If a per-login identifier lands on /auth/me, bind to it here instead.
 *
 * Multiple hook instances (the card and its host page) share one dismissal via
 * the subscribe/notify pair below, so they never disagree.
 */

const DISMISS_KEY = "strenn-memory-confirm-card-dismissed";

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** useSyncExternalStore subscription: fires after every write/clear. */
export function subscribeConfirmCardDismissed(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True only when THIS user dismissed the card in THIS login. */
export function readConfirmCardDismissed(userId: string): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === userId;
  } catch {
    return false;
  }
}

export function writeConfirmCardDismissed(userId: string): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, userId);
  } catch {
    // Storage disabled or quota exceeded — degrade silently.
  }
  notify();
}

/** Release the dismissal: the login it belonged to has ended. */
export function clearConfirmCardDismissed(): void {
  try {
    sessionStorage.removeItem(DISMISS_KEY);
  } catch {
    // Storage disabled — nothing to clear.
  }
  notify();
}
